(function () {
  'use strict';

  if (customElements.get('theme-video')) return;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var viewportObserver = null;
  var autoplayObserver = null;

  function ensureViewportObserver() {
    if (viewportObserver || !('IntersectionObserver' in window)) return viewportObserver;
    viewportObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var player = entry.target;
        if (entry.isIntersecting) player.play();
        else player.pause();
      });
    }, { threshold: 0.05 });
    return viewportObserver;
  }

  // Autoplay videos start at once, as before, but pause while they are off
  // screen so a looping video far down the page does not keep every frame busy.
  function ensureAutoplayObserver() {
    if (autoplayObserver || !('IntersectionObserver' in window)) return autoplayObserver;
    autoplayObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var player = entry.target;
        if (entry.isIntersecting) player.play();
        else if (player.dataset.loaded === 'true') player.pause();
      });
    }, { rootMargin: '200px 0px' });
    return autoplayObserver;
  }

  class ThemeVideo extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.mode = this.dataset.playbackMode || 'manual';
      this.media = this.querySelector('video, iframe');
      if (!this.media) return;

      if (this.mode === 'manual') {
        this.load();
        return;
      }

      if (reducedMotion.matches) {
        if (this.mode !== 'viewport') this.load(false);
        return;
      }

      if (this.mode === 'viewport') {
        var observer = ensureViewportObserver();
        if (observer) observer.observe(this);
        else this.play();
        return;
      }

      this.play();
      var autoplayWatch = ensureAutoplayObserver();
      if (autoplayWatch) autoplayWatch.observe(this);
    }

    disconnectedCallback() {
      if (viewportObserver) viewportObserver.unobserve(this);
      if (autoplayObserver) autoplayObserver.unobserve(this);
      this.pause();
      this._connected = false;
    }

    load(allowAutoplay) {
      if (!this.media || this.dataset.loaded === 'true') return;
      this.dataset.loaded = 'true';
      if (this.media.tagName === 'IFRAME') {
        var source = this.media.dataset.themeVideoSrc;
        if (source) {
          if (allowAutoplay === false) {
            try {
              var url = new URL(source, window.location.href);
              url.searchParams.set('autoplay', '0');
              source = url.toString();
            } catch (error) {
              source = source.replace(/([?&])autoplay=1(?:&|$)/, '$1autoplay=0&').replace(/&$/, '');
            }
          }
          this.media.src = source;
          this.media.removeAttribute('data-theme-video-src');
        }
      } else {
        this.media.querySelectorAll('source[data-theme-video-src]').forEach(function (source) {
          source.src = source.dataset.themeVideoSrc;
          source.removeAttribute('data-theme-video-src');
        });
        this.media.load();
      }
    }

    play() {
      if (!this.media || reducedMotion.matches) return;
      this.load();
      if (this.media.tagName === 'VIDEO') {
        this.media.play().catch(function () {});
        return;
      }
      this.postMessage('play');
    }

    pause() {
      if (!this.media) return;
      if (this.media.tagName === 'VIDEO') {
        this.media.pause();
        return;
      }
      this.postMessage('pause');
    }

    postMessage(action) {
      if (!this.media || !this.media.contentWindow || !this.media.src) return;
      var isVimeo = this.media.src.indexOf('vimeo.com') !== -1;
      var payload = isVimeo
        ? { method: action }
        : { event: 'command', func: action === 'play' ? 'playVideo' : 'pauseVideo', args: [] };
      this.media.contentWindow.postMessage(JSON.stringify(payload), '*');
    }
  }

  customElements.define('theme-video', ThemeVideo);
})();
